import React, { useRef, useState, useEffect } from 'react';
import { Download, Loader2, FileText } from 'lucide-react';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { BrochureCampaignContent } from './BrochureCampaignContent';
import { BrochureStory } from './BrochureStory';

const API = process.env.REACT_APP_BACKEND_URL;

const BrochureGenerator = () => {
  const brochureRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [assets, setAssets] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/brochure-assets`)
      .then(res => setAssets(res.data))
      .catch(() => setAssets({}));
  }, []);

  const captureDataUrl = async () => {
    if (!brochureRef.current) throw new Error('Ref bulunamadi');
    return toPng(brochureRef.current, {
      pixelRatio: 3,
      backgroundColor: '#F5EDE4',
      cacheBust: true,
      includeQueryParams: true,
      skipAutoScale: true
    });
  };

  const handleDownloadPng = async () => {
    setDownloading(true);
    try {
      const dataUrl = await captureDataUrl();
      const link = document.createElement('a');
      link.download = 'central-hotel-brosur-a6.png';
      link.href = dataUrl;
      link.click();
      toast.success('Brosur PNG indirildi!');
    } catch (err) {
      console.error('PNG error:', err);
      toast.error('PNG indirilemedi: ' + (err.message || ''));
    } finally { setDownloading(false); }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const dataUrl = await captureDataUrl();
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const bW = 99, bH = 140, mx = 6, my = 8;
      pdf.addImage(dataUrl, 'PNG', mx, my, bW, bH);
      pdf.addImage(dataUrl, 'PNG', mx + bW + mx, my, bW, bH);
      pdf.addImage(dataUrl, 'PNG', mx, my + bH + my, bW, bH);
      pdf.addImage(dataUrl, 'PNG', mx + bW + mx, my + bH + my, bW, bH);
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineDashPattern([2, 2], 0);
      pdf.line(mx + bW + mx / 2, 0, mx + bW + mx / 2, 297);
      pdf.line(0, my + bH + my / 2, 210, my + bH + my / 2);
      pdf.save('central-hotel-brosur-a4-baski.pdf');
      toast.success('A4 PDF indirildi! 4 brosur.');
    } catch (err) {
      console.error('PDF error:', err);
      toast.error('PDF olusturulamadi: ' + (err.message || ''));
    } finally { setDownloadingPdf(false); }
  };

  if (!assets) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-6 h-6 animate-spin text-terracotta mr-2" />
        <span className="text-charcoal/60">Gorseller yukleniyor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-playfair text-xl font-semibold text-charcoal">El Broşürü (A6)</h2>
          <p className="text-sm text-charcoal/60">"4 Çeşit Yemek €10" kampanyası · 4 dilli · sipariş ve konum QR'lı</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleDownloadPng} disabled={downloading} variant="outline" className="rounded-full" data-testid="brochure-download-png">
            {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            PNG
          </Button>
          <Button onClick={handleDownloadPdf} disabled={downloadingPdf} className="bg-red-700 text-white hover:bg-red-600 rounded-full" data-testid="brochure-download-pdf">
            {downloadingPdf ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
            {downloadingPdf ? 'Hazirlaniyor...' : 'A4 PDF Indir (Baskiya Hazir)'}
          </Button>
        </div>
      </div>

      <div className="flex justify-center">
        <div
          ref={brochureRef}
          data-testid="brochure-canvas"
          style={{
            width: '420px', height: '595px', background: '#F5EDE4',
            fontFamily: "'Georgia', 'Times New Roman', serif",
            position: 'relative', overflow: 'hidden', borderRadius: '4px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
          }}
        >
          <BrochureCampaignContent assets={assets} />
        </div>
      </div>

      <div className="text-center text-sm text-charcoal/40">
        A6 (105x148mm) | PNG tek brosur | PDF A4 sayfada 4 brosur baskiya hazir
      </div>
      <BrochureStory assets={assets} />
    </div>
  );
};

export default BrochureGenerator;
